import { useState, useEffect } from 'react';
import { X, User, Search, CheckCircle, UserPlus, Calendar, Users } from 'lucide-react';
import { Room, Guest, CreateGuest } from '@/shared/types';
import { apiGet, apiPost } from '@/react-app/utils/api';

interface ReservationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRefresh?: () => void;
  preSelectedRoom?: Room | null;
  preSelectedDate?: string;
}

export default function ReservationModal({ isOpen, onClose, onRefresh, preSelectedRoom, preSelectedDate }: ReservationModalProps) {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [selectedGuestId, setSelectedGuestId] = useState<number | null>(null);
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(preSelectedRoom?.id || null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showNewGuestForm, setShowNewGuestForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<'guest' | 'room' | 'details'>('guest');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [newGuest, setNewGuest] = useState<CreateGuest>({
    name: '',
    document: '',
    phone: '',
    email: '',
    address: '',
    guest_type: 'individual',
    company_name: '',
    cnpj: '',
    company_email: '',
    company_phone: ''
  });

  const [reservationData, setReservationData] = useState({
    check_in_date: preSelectedDate || new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    check_out_date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    adults: 1,
    children: 0,
    discount: 0,
    notes: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchData();
      if (preSelectedRoom) {
        setSelectedRoomId(preSelectedRoom.id);
        setCurrentStep('guest');
      }
      if (preSelectedDate) {
        setReservationData(prev => ({
          ...prev,
          check_in_date: preSelectedDate,
          check_out_date: new Date(new Date(preSelectedDate).getTime() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        }));
      }
    }
  }, [isOpen, preSelectedRoom, preSelectedDate]);

  const fetchData = async () => {
    try {
      const [roomsResponse, guestsResponse] = await Promise.all([
        apiGet('/api/rooms'),
        apiGet('/api/guests')
      ]);
      
      const roomsData = await roomsResponse.json();
      const guestsData = await guestsResponse.json();
      
      setRooms(roomsData);
      setGuests(guestsData);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleCreateGuest = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await apiPost('/api/guests', newGuest);

      if (response.ok) {
        const guest = await response.json();
        setSelectedGuestId(guest.id);
        setShowNewGuestForm(false);
        setNewGuest({ 
          name: '', document: '', phone: '', email: '', address: '',
          guest_type: 'individual', company_name: '', cnpj: '', company_email: '', company_phone: '' 
        });
        await fetchData();
      }
    } catch (error) {
      console.error('Error creating guest:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateReservation = async () => {
    if (!selectedGuestId || !selectedRoomId) return;
    setIsLoading(true);
    setErrorMessage('');

    try {
      const response = await apiPost('/api/reservations', {
        guest_id: selectedGuestId,
        room_id: selectedRoomId,
        ...reservationData,
        daily_rate: priceBreakdown.dailyRate,
        total_amount: priceBreakdown.finalTotal
      });

      if (response.ok) {
        onRefresh?.();
        onClose();
        resetForm();
      } else {
        const errorData = await response.json();
        setErrorMessage(errorData.message || errorData.error || 'Erro ao criar reserva');
      }
    } catch (error) {
      console.error('Error creating reservation:', error);
      setErrorMessage('Erro ao criar reserva. Por favor, tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedGuestId(null);
    setSelectedRoomId(preSelectedRoom?.id || null);
    setSearchTerm('');
    setShowNewGuestForm(false);
    setCurrentStep('guest');
    setErrorMessage('');
    setReservationData({
      check_in_date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      check_out_date: new Date(Date.now() + 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      adults: 1,
      children: 0,
      discount: 0,
      notes: ''
    });
  };

  const filteredGuests = guests.filter(guest => 
    guest.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    guest.document.includes(searchTerm) ||
    guest.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedRoom = rooms.find(room => room.id === selectedRoomId);
  const selectedGuest = guests.find(guest => guest.id === selectedGuestId);

  const canProceedToNextStep = () => {
    if (currentStep === 'guest') return selectedGuestId;
    if (currentStep === 'room') return selectedRoomId;
    return true;
  };

  // Calculate total stay cost: daily rate × number of nights
  const calculateStayCost = (adults: number, children: number, discount: number = 0, checkIn: string, checkOut: string) => {
    // Fixed rates by occupancy type
    let dailyRate = 130.00; // Solteiro (1 pessoa)
    
    if (adults === 2) {
      dailyRate = 170.00; // Casal
    } else if (adults >= 3) {
      dailyRate = 210.00; // Triplo ou mais
    }
    
    // Calculate number of nights
    const checkInDate = new Date(checkIn);
    const checkOutDate = new Date(checkOut);
    const timeDiff = checkOutDate.getTime() - checkInDate.getTime();
    const nights = Math.max(1, Math.ceil(timeDiff / (1000 * 60 * 60 * 24)));
    
    // Add children cost (R$ 20 per child)
    const childrenCost = children * 20.00;
    
    // Apply discount per night to the base daily rate (not children cost)
    const discountedDailyRate = Math.max(0, dailyRate - discount);
    const totalDailyRate = discountedDailyRate + childrenCost;
    
    const totalBeforeDiscount = (dailyRate + childrenCost) * nights;
    const finalTotal = totalDailyRate * nights;
    const totalDiscount = discount * nights;
    
    return {
      baseRate: dailyRate,
      childrenCost,
      adults,
      children,
      nights,
      dailyRate: totalDailyRate,
      totalBeforeDiscount,
      discount: totalDiscount,
      finalTotal,
      discountPerNight: discount
    };
  };

  const priceBreakdown = calculateStayCost(
    reservationData.adults, 
    reservationData.children, 
    reservationData.discount || 0,
    reservationData.check_in_date,
    reservationData.check_out_date
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />
        
        <div className="inline-block w-full max-w-4xl p-8 my-8 overflow-hidden text-left align-middle transition-all transform bg-white dark:bg-gray-800 shadow-2xl rounded-3xl">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                Nova Reserva
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Crie uma nova reserva em 3 passos simples
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between mb-8 px-4">
            {[
              { key: 'guest', label: 'Hóspede', icon: User },
              { key: 'room', label: 'Quarto', icon: Calendar },
              { key: 'details', label: 'Detalhes', icon: CheckCircle }
            ].map((step, index) => (
              <div key={step.key} className="flex items-center">
                <div className={`flex items-center justify-center w-12 h-12 rounded-full border-2 transition-all ${
                  currentStep === step.key || (index < ['guest', 'room', 'details'].indexOf(currentStep))
                    ? 'bg-blue-600 border-blue-600 text-white'
                    : 'border-gray-300 text-gray-400'
                }`}>
                  <step.icon className="w-6 h-6" />
                </div>
                <div className="ml-3">
                  <p className={`text-sm font-medium ${
                    currentStep === step.key ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500'
                  }`}>
                    Passo {index + 1}
                  </p>
                  <p className={`text-sm ${
                    currentStep === step.key ? 'text-gray-900 dark:text-white' : 'text-gray-500'
                  }`}>
                    {step.label}
                  </p>
                </div>
                {index < 2 && (
                  <div className={`ml-8 w-16 h-0.5 ${
                    index < ['guest', 'room', 'details'].indexOf(currentStep)
                      ? 'bg-blue-600'
                      : 'bg-gray-300'
                  }`} />
                )}
              </div>
            ))}
          </div>

          {/* Step Content */}
          <div className="min-h-[400px]">
            {currentStep === 'guest' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h4 className="text-xl font-semibold text-gray-900 dark:text-white">Selecionar Hóspede</h4>
                  <button
                    onClick={() => setShowNewGuestForm(!showNewGuestForm)}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
                  >
                    <UserPlus className="w-5 h-5" />
                    <span>Novo Hóspede</span>
                  </button>
                </div>

                {showNewGuestForm && (
                  <form onSubmit={handleCreateGuest} className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-600 p-6 rounded-xl space-y-6">
                    <h5 className="font-semibold text-gray-900 dark:text-white mb-4">Cadastrar Novo Hóspede</h5>
                    
                    {/* Guest Type Selection */}
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Tipo de Hóspede
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <button
                          type="button"
                          onClick={() => setNewGuest({ 
                            ...newGuest, 
                            guest_type: 'individual',
                            company_name: '', cnpj: '', company_email: '', company_phone: ''
                          })}
                          className={`p-4 border-2 rounded-xl text-center transition-all ${
                            newGuest.guest_type === 'individual'
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                          }`}
                        >
                          <div className="font-medium">Pessoa Física</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">Hóspede individual</div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setNewGuest({ ...newGuest, guest_type: 'corporate' })}
                          className={`p-4 border-2 rounded-xl text-center transition-all ${
                            newGuest.guest_type === 'corporate'
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
                          }`}
                        >
                          <div className="font-medium">Pessoa Jurídica</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">Funcionário de empresa</div>
                        </button>
                      </div>
                    </div>

                    {/* Individual Guest Fields */}
                    {newGuest.guest_type === 'individual' && (
                      <div className="space-y-4">
                        <h6 className="font-medium text-gray-900 dark:text-white">Dados Pessoais</h6>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <input
                            type="text"
                            placeholder="Nome completo"
                            value={newGuest.name}
                            onChange={(e) => setNewGuest({ ...newGuest, name: e.target.value })}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all"
                            required
                          />
                          <input
                            type="text"
                            placeholder="CPF/RG"
                            value={newGuest.document}
                            onChange={(e) => setNewGuest({ ...newGuest, document: e.target.value })}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all"
                            required
                          />
                          <input
                            type="tel"
                            placeholder="Telefone"
                            value={newGuest.phone}
                            onChange={(e) => setNewGuest({ ...newGuest, phone: e.target.value })}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all"
                          />
                          <input
                            type="email"
                            placeholder="Email"
                            value={newGuest.email}
                            onChange={(e) => setNewGuest({ ...newGuest, email: e.target.value })}
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all"
                          />
                        </div>
                        <input
                          type="text"
                          placeholder="Endereço"
                          value={newGuest.address}
                          onChange={(e) => setNewGuest({ ...newGuest, address: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all"
                        />
                      </div>
                    )}

                    {/* Corporate Guest Fields */}
                    {newGuest.guest_type === 'corporate' && (
                      <div className="space-y-6">
                        <div>
                          <h6 className="font-medium text-gray-900 dark:text-white mb-4">Dados da Empresa</h6>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <input
                              type="text"
                              placeholder="Nome da empresa"
                              value={newGuest.company_name || ''}
                              onChange={(e) => setNewGuest({ ...newGuest, company_name: e.target.value })}
                              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all"
                              required
                            />
                            <input
                              type="text"
                              placeholder="CNPJ"
                              value={newGuest.cnpj || ''}
                              onChange={(e) => setNewGuest({ ...newGuest, cnpj: e.target.value })}
                              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all"
                              required
                            />
                            <input
                              type="tel"
                              placeholder="Telefone da empresa"
                              value={newGuest.company_phone || ''}
                              onChange={(e) => setNewGuest({ ...newGuest, company_phone: e.target.value })}
                              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all"
                              required
                            />
                            <input
                              type="email"
                              placeholder="Email da empresa"
                              value={newGuest.company_email || ''}
                              onChange={(e) => setNewGuest({ ...newGuest, company_email: e.target.value })}
                              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all"
                              required
                            />
                          </div>
                        </div>
                        
                        <div>
                          <h6 className="font-medium text-gray-900 dark:text-white mb-4">Dados do Funcionário</h6>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <input
                              type="text"
                              placeholder="Nome do funcionário"
                              value={newGuest.name}
                              onChange={(e) => setNewGuest({ ...newGuest, name: e.target.value })}
                              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all"
                              required
                            />
                            <input
                              type="text"
                              placeholder="CPF do funcionário"
                              value={newGuest.document}
                              onChange={(e) => setNewGuest({ ...newGuest, document: e.target.value })}
                              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all"
                              required
                            />
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex space-x-3 pt-4 border-t border-gray-200 dark:border-gray-600">
                      <button
                        type="submit"
                        disabled={isLoading}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium disabled:opacity-50 transition-all"
                      >
                        {isLoading ? 'Criando...' : 'Criar Hóspede'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowNewGuestForm(false)}
                        className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-medium transition-all"
                      >
                        Cancelar
                      </button>
                    </div>
                  </form>
                )}

                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Buscar hóspede por nome, documento ou email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all"
                  />
                </div>

                <div className="max-h-80 overflow-y-auto space-y-3">
                  {filteredGuests.map((guest) => (
                    <div
                      key={guest.id}
                      onClick={() => setSelectedGuestId(guest.id)}
                      className={`p-4 border-2 rounded-xl cursor-pointer transition-all hover:shadow-md ${
                        selectedGuestId === guest.id
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-md'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold text-gray-900 dark:text-white">{guest.name}</p>
                            {guest.guest_type === 'corporate' && (
                              <span className="px-2 py-1 text-xs bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded-full">
                                Empresa
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{guest.document}</p>
                          {guest.guest_type === 'corporate' && guest.company_name && (
                            <p className="text-sm text-purple-600 dark:text-purple-400 font-medium">{guest.company_name}</p>
                          )}
                          {guest.email && <p className="text-sm text-gray-600 dark:text-gray-400">{guest.email}</p>}
                        </div>
                        {selectedGuestId === guest.id && (
                          <CheckCircle className="w-6 h-6 text-blue-500" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {currentStep === 'room' && !preSelectedRoom && (
              <div className="space-y-6">
                <h4 className="text-xl font-semibold text-gray-900 dark:text-white">Selecionar Quarto</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {rooms.filter(room => room.is_active).map((room) => (
                    <div
                      key={room.id}
                      onClick={() => setSelectedRoomId(room.id)}
                      className={`p-6 border-2 rounded-xl cursor-pointer transition-all hover:shadow-md ${
                        selectedRoomId === room.id
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-md'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h5 className="font-semibold text-gray-900 dark:text-white">
                            Quarto {room.room_number}
                          </h5>
                          <p className="text-sm text-gray-600 dark:text-gray-400">{room.room_type}</p>
                        </div>
                        {selectedRoomId === room.id && (
                          <CheckCircle className="w-6 h-6 text-blue-500" />
                        )}
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600 dark:text-gray-400 flex items-center">
                          <Users className="w-4 h-4 mr-1" />
                          {room.max_occupancy} pessoas
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {currentStep === 'details' && (
              <div className="space-y-6">
                <h4 className="text-xl font-semibold text-gray-900 dark:text-white">Detalhes da Reserva</h4>
                
                {/* Summary */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-600 p-6 rounded-xl">
                  <h5 className="font-semibold text-gray-900 dark:text-white mb-4">Resumo da Reserva</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Hóspede</p>
                      <p className="font-medium text-gray-900 dark:text-white">{selectedGuest?.name}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Quarto</p>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {selectedRoom?.room_number} - {selectedRoom?.room_type}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Check-in
                    </label>
                    <input
                      type="date"
                      value={reservationData.check_in_date}
                      onChange={(e) => setReservationData({ ...reservationData, check_in_date: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Check-out
                    </label>
                    <input
                      type="date"
                      value={reservationData.check_out_date}
                      onChange={(e) => setReservationData({ ...reservationData, check_out_date: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Adultos
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={reservationData.adults}
                      onChange={(e) => setReservationData({ ...reservationData, adults: parseInt(e.target.value) || 1 })}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Crianças
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={reservationData.children}
                      onChange={(e) => setReservationData({ ...reservationData, children: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Desconto (R$)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={reservationData.discount}
                      onChange={(e) => setReservationData({ ...reservationData, discount: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all"
                    />
                  </div>

                  {/* Cálculo automático de preços */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">Cálculo da Estadia</h4>
                    <div className="space-y-1 text-sm text-blue-800 dark:text-blue-200">
                      <div className="flex justify-between">
                        <span>Período:</span>
                        <span>{priceBreakdown.nights} {priceBreakdown.nights === 1 ? 'diária' : 'diárias'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Tipo de ocupação ({priceBreakdown.adults} {priceBreakdown.adults === 1 ? 'adulto' : 'adultos'}):</span>
                        <span>R$ {priceBreakdown.baseRate.toFixed(2)}/dia</span>
                      </div>
                      {priceBreakdown.children > 0 && (
                        <div className="flex justify-between">
                          <span>Crianças ({priceBreakdown.children}x R$ 20/dia):</span>
                          <span>R$ {priceBreakdown.childrenCost.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-medium">
                        <span>Subtotal por dia:</span>
                        <span>R$ {priceBreakdown.dailyRate.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between font-medium">
                        <span>Total ({priceBreakdown.nights} {priceBreakdown.nights === 1 ? 'dia' : 'dias'}):</span>
                        <span>R$ {priceBreakdown.totalBeforeDiscount.toFixed(2)}</span>
                      </div>
                      {priceBreakdown.discount > 0 && (
                        <div className="flex justify-between text-red-600 dark:text-red-400">
                          <span>Desconto (R$ {priceBreakdown.discountPerNight.toFixed(2)}/dia):</span>
                          <span>- R$ {priceBreakdown.discount.toFixed(2)}</span>
                        </div>
                      )}
                      <hr className="border-blue-200 dark:border-blue-700" />
                      <div className="flex justify-between font-bold text-lg">
                        <span>Total Final:</span>
                        <span>R$ {priceBreakdown.finalTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Observações
                  </label>
                  <textarea
                    value={reservationData.notes}
                    onChange={(e) => setReservationData({ ...reservationData, notes: e.target.value })}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 transition-all"
                    placeholder="Observações adicionais sobre a reserva..."
                  />
                </div>
              </div>
            )}
          </div>

          {/* Error Message */}
          {errorMessage && (
            <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                    Erro ao criar reserva
                  </h3>
                  <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                    <p>{errorMessage}</p>
                  </div>
                </div>
                <div className="ml-auto pl-3">
                  <div className="-mx-1.5 -my-1.5">
                    <button
                      onClick={() => setErrorMessage('')}
                      className="inline-flex rounded-md p-1.5 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/50 focus:outline-none"
                    >
                      <span className="sr-only">Dismiss</span>
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Footer */}
          <div className="flex justify-between items-center pt-8 border-t border-gray-200 dark:border-gray-700">
            <div>
              {currentStep !== 'guest' && (
                <button
                  onClick={() => {
                    if (currentStep === 'room') setCurrentStep('guest');
                    if (currentStep === 'details') setCurrentStep(preSelectedRoom ? 'guest' : 'room');
                  }}
                  className="px-6 py-3 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all font-medium"
                >
                  Voltar
                </button>
              )}
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-6 py-3 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all font-medium"
              >
                Cancelar
              </button>
              
              {currentStep === 'details' ? (
                <button
                  onClick={handleCreateReservation}
                  disabled={isLoading || !canProceedToNextStep()}
                  className="px-8 py-3 text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
                >
                  {isLoading ? 'Criando Reserva...' : 'Criar Reserva'}
                </button>
              ) : (
                <button
                  onClick={() => {
                    if (currentStep === 'guest') setCurrentStep(preSelectedRoom ? 'details' : 'room');
                    if (currentStep === 'room') setCurrentStep('details');
                  }}
                  disabled={!canProceedToNextStep()}
                  className="px-8 py-3 text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium"
                >
                  Próximo
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
